import type { WithoutIdAndReview, ReviewState } from "@briefmarken_katalog/types";
import type { TranslationKey } from "$lib/i18n";
import { EditorModelBase } from "./EditorModelBase.svelte";

export abstract class StandardEditorModelBase<TItem> extends EditorModelBase {
  isReadonly: boolean;
  isReview: boolean;
  isDelete: boolean;
  isEdit: boolean;
  showSubmitButton: boolean;
  dialogTitle = $state<TranslationKey>('' as TranslationKey);
  dialogSubtitle = $state<TranslationKey>('' as TranslationKey);
  dialogSubtitleSuffix = $state('');
  initialSnapshot: string | null = null;
  isDirty = $derived.by(() => this.computeIsDirty());
  onCreate: (item: WithoutIdAndReview<TItem>) => void;
  onReview: (item: WithoutIdAndReview<TItem>, state: ReviewState) => void;
  onDelete: () => void;

  constructor({
    onCreate,
    onReview,
    onDelete = () => {},
    isReadonly = false,
    isReview = false,
    isDelete = false,
    isEdit = false,
    hasInitialValue = false,
    dialogTitle,
    dialogSubtitle,
  }: {
    onCreate: (item: WithoutIdAndReview<TItem>) => void;
    onReview: (item: WithoutIdAndReview<TItem>, state: ReviewState) => void;
    onDelete?: () => void;
    isReadonly?: boolean;
    isReview?: boolean;
    isDelete?: boolean;
    isEdit?: boolean;
    hasInitialValue?: boolean;
    dialogTitle: TranslationKey;
    dialogSubtitle: TranslationKey;
  }) {
    super();
    this.onCreate = onCreate;
    this.onReview = onReview;
    this.onDelete = onDelete;
    this.isReadonly = isDelete ? true : isReadonly;
    this.isReview = isReview;
    this.isDelete = isDelete;
    this.isEdit = isEdit;
    this.showSubmitButton = !hasInitialValue || isEdit;
    this.dialogTitle = dialogTitle;
    this.dialogSubtitle = dialogSubtitle;
  }

  abstract buildItem(): WithoutIdAndReview<TItem>;
  abstract reset(): void;

  protected computeIsDirty(): boolean {
    return this.initialSnapshot === null || JSON.stringify(this.buildItem()) !== this.initialSnapshot;
  }

  onsubmit(close: () => void, ev: Event) {
    ev.preventDefault();
    if (!this.isValid) return;
    close();
    this.onCreate(this.buildItem());
    this.reset();
  }

  onreview(state: ReviewState, close: () => void) {
    close();
    this.onReview(this.buildItem(), state);
  }

  ondelete(close: () => void) {
    close();
    this.onDelete();
  }
}
